import React, { useState } from 'react';
import { CrudPage } from '../../components/CrudPage';
import type { ResourceDto } from '../../api/models/ResourceDto';
import { ResourceControllerService } from '../../api';
import { useErrorHandler } from '../../utils/errorHandler';
import ResourceFormDialog from '../forms/ResourceFormDialog';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../hooks/useToast';

// Type with string id for CrudPage compatibility
type ResourceWithStringId = Omit<ResourceDto, 'id'> & { id: string; position?: number };

const ResourcesPage = () => {
  const { handleError } = useErrorHandler();
  const { showToast } = useToast();
  const { t } = useTranslation(['common']);
  const [refreshKey, setRefreshKey] = useState(0);

  React.useEffect(() => {
    const handleRefresh = () => {
      setRefreshKey(prev => prev + 1);
    };
    window.addEventListener('resources:refresh', handleRefresh as EventListener);
    return () => {
      window.removeEventListener('resources:refresh', handleRefresh as EventListener);
    };
  }, []);

  // Convert CancelablePromise to Promise for CrudPage compatibility
  const fetchData = async (): Promise<ResourceWithStringId[]> => {
    try {
      const resources = await ResourceControllerService.findAll();
      // Null-check: Ensure resources is an array
      if (!resources || !Array.isArray(resources)) {
        return [];
      }
      // Filter out resources with undefined uuid and convert uuid to string
      // Sort by position (null values last), then by name
      const filteredResources = resources
        .filter((resource): resource is ResourceDto => resource && typeof resource.uuid === 'string')
        .map(resource => ({
          ...resource,
          id: resource.uuid as unknown as string, // Cast to string since API returns string
          position: (resource as any).position
        }))
        .sort((a, b) => {
          // Sort by position (null values last), then by name
          if (a.position == null && b.position == null) {
            return (a.name || '').localeCompare(b.name || '');
          }
          if (a.position == null) return 1;
          if (b.position == null) return -1;
          return a.position - b.position;
        });
      return filteredResources;
    } catch (error) {
      handleError(error, 'fetching resources');
      return [];
    }
  };

  const saveData = async (data: ResourceWithStringId): Promise<void> => {
    try {
      if (data.id && data.id.length > 0) {
        // Update existing resource
        // Convert back to ResourceDto format for the API
        const resourceDto: ResourceDto = {
          ...data,
          uuid: data.id // Use the string id as uuid
        };
        await ResourceControllerService.update(data.id, resourceDto);
      } else {
        // Create new resource
        // Convert back to ResourceDto format for the API
        const resourceDto: ResourceDto = {
          ...data,
          uuid: undefined // New resources don't have uuid yet
        };
        await ResourceControllerService.create(resourceDto);
      }
    } catch (error) {
      handleError(error, 'saving resource');
      throw error;
    }
  };

  const deleteData = async (id: string): Promise<void> => {
    try {
      await ResourceControllerService.delete(id);
    } catch (error) {
      handleError(error, 'deleting resource');
      throw error;
    }
  };

  // Handle priority change (drag & drop reordering)
  const handlePriorityChange = async (items: ResourceWithStringId[]): Promise<void> => {
    try {
      // Update position for all resources based on their new order
      const updatePromises = items.map((resource, index) => {
        const resourceDto: ResourceDto = {
          ...resource,
          uuid: resource.id,
          position: index + 1
        } as any;
        return ResourceControllerService.update(resource.id, resourceDto);
      });
      
      await Promise.all(updatePromises);
      showToast('success', t('common.saved', 'Gespeichert'), '');
      // Don't trigger refreshKey update here - the CrudPage already updated the local state
      // Only refresh if we need to reload from server (which we don't need to do immediately)
      // The local state update in CrudPage is sufficient
    } catch (error) {
      handleError(error, 'updating resource positions');
      throw error;
    }
  };

  // Custom renderer for shift assignments - only show count
  const shiftAssignmentsRenderer = (data: ResourceWithStringId) => {
    const assignments = data.shiftAssignments ?? [];
    const hasAssignments = assignments.length > 0;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
        <span>{assignments.length}</span>
        {!hasAssignments && (
          <span title={t('resources.noShiftAssignments', 'Keine Schichtzuweisungen')}>
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              role="img"
              aria-label={t('resources.noShiftAssignments', 'Keine Schichtzuweisungen')}
              style={{ color: '#d97706' }}
            >
              <path
                fill="currentColor"
                d="M12 3L1 21h22L12 3zm0 5.5c.6 0 1 .4 1 1v6a1 1 0 0 1-2 0v-6c0-.6.4-1 1-1zm0 9.5a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5z"
              />
            </svg>
          </span>
        )}
      </span>
    );
  };

  return (
    <CrudPage<ResourceWithStringId>
      titleKey="routes.resources"
      gridMeta={[
        { field: 'name', headerKey: 'grid.name', width: 150 },
        { field: 'description', headerKey: 'grid.description', width: 200 },
        { field: 'resourceTypeName', headerKey: 'grid.resourceType', width: 150 },
        { 
          field: 'shiftAssignments', 
          headerKey: 'grid.shiftAssignments', 
          width: 150,
          align: 'Center',
          render: shiftAssignmentsRenderer
        }
      ]}
      fetchData={fetchData}
      saveData={saveData}
      deleteData={deleteData}
      FormComponent={ResourceFormDialog}
      entityName="resource"
      hasPriority={true}
      priorityField="position"
      onPriorityChange={handlePriorityChange}
      refreshKey={refreshKey}
      allowSorting={false}
    />
  );
};

export default ResourcesPage;
